// Global fetch interceptor that attaches the Supabase identity to every
// same-origin /api request, so the daemon can authenticate the user and scope
// data to their tenant. Installing one wrapper here covers every existing
// fetch('/api/...') call site (the whole of `providers/daemon.ts`) and any
// future one without touching them.
//
// What it sends — and deliberately does not: ONLY `Authorization: Bearer
// <access_token>`. v1 also sent `X-Org-Id`; v2 drops it (decision D1). The
// daemon derives the tenant from the verified token's subject server-side, so a
// client-chosen org is unnecessary and would reopen the IDOR surface D1 closed.
//
// Native EventSource streams (project/memory/library events) cannot carry a
// header, so they are NOT covered here — authenticating those is a follow-up
// (a query-param scoped token like the existing preview-asset path, or
// fetch-based streaming), tracked with the item #3 non-bearer surfaces.
//
// When there is no session (logged out, or Supabase not configured) the request
// goes out unmodified, preserving the open local/desktop behavior.

import { supabase, supabaseConfigured } from './supabaseClient';

export function isApiUrl(url: string): boolean {
  if (url.startsWith('/api/')) return true;
  if (typeof location !== 'undefined') {
    return url.startsWith(`${location.origin}/api/`);
  }
  return false;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * The headers to send for an authenticated /api request, or null meaning "leave
 * the request untouched" (no session). Adds the bearer only when the caller has
 * not already set one, and never adds an org header — the daemon resolves the
 * tenant from the token alone (D1).
 */
export function buildAuthHeaders(
  existing: HeadersInit | undefined,
  token: string | null,
): Headers | null {
  if (!token) return null;
  const headers = new Headers(existing);
  if (!headers.has('authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

let installed = false;

export function installApiAuthInterceptor(): void {
  if (installed || typeof window === 'undefined' || !supabaseConfigured || !supabase) {
    return;
  }
  installed = true;
  const originalFetch = window.fetch.bind(window);

  // A 402 from the daemon means the tenant has no active subscription/trial or
  // hit its token cap. Surface it app-wide so the shell can react (billing is
  // roadmap item #4); harmless until then — nothing listens yet.
  function emitOn402(res: Response): Response {
    if (res.status === 402) {
      window.dispatchEvent(new CustomEvent('crie3:billing-required'));
    }
    return res;
  }

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const isApi = isApiUrl(requestUrl(input));
    try {
      if (isApi) {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token ?? null;
        const headers = buildAuthHeaders(
          init?.headers ?? (input instanceof Request ? input.headers : undefined),
          token,
        );
        if (headers) {
          return emitOn402(await originalFetch(input, { ...init, headers }));
        }
      }
    } catch {
      // Never let auth wiring break a request — fall through to the original.
    }
    const res = await originalFetch(input, init);
    return isApi ? emitOn402(res) : res;
  };
}
